import { readReturnToFromStorage, syncReturnToStorage } from './returnToStorage';
import { syncPhpliveVisitorPrefill } from './phplivePrefill';
import { syncNewsletterFormPrefill } from './newsletterPrefill';

type Listener = () => void;
const listeners: Listener[] = [];

let auth: { login?: string; name?: string; suc_count?: number; bonus_account?: number; customer_uid?: string; customer_email?: string } | null = null;
let cartCount = 0;
let cartTotal = 0;
let cartProducts: any[] = [];
let loading = true;
export type AccountView = 'discounts' | 'bonus-transactions' | 'account' | 'bonus-account' | 'referral-tree' | 'orders' | 'subscriptions' | 'tickets' | 'account-delete' | 'signup' | 'cart' | 'checkout' | 'checkout-qnr' | 'checkout-confirm' | 'order-success' | 'order-return' | 'checkout-auth' | 'checkout-login' | 'catalog' | 'forgot-password' | 'reset-password' | null;
export type OrderReturnIntent = 'success' | 'cancel' | 'failure' | null;
let accountView: AccountView = null;
let returnTo: AccountView = null;
let paymentDetails: Record<string, string> | null = null;
let lastOrderId: string | null = null;
let orderReturnIntent: OrderReturnIntent = null;
/** When set, OrdersView opens this order detail on next mount (e.g. from payment return). */
let ordersFocusOrderId: string | null = null;

export interface Toast {
  message: string;
  type: 'success' | 'error' | 'info';
  id: number;
}
let toasts: Toast[] = [];
let oldQnrData: Record<string, unknown> | null = null;
let settings: any = {};
let checkoutPaymentId: string | null = null;
const inlinePlaceholders = new Set<NonNullable<AccountView>>();

export const store = {
  getSettings: () => settings,
  setSettings: (v: any) => {
    settings = v;
    listeners.forEach((f) => f());
  },
  getOldQnrData: () => oldQnrData,
  setOldQnrData: (v: Record<string, unknown> | null) => {
    oldQnrData = v;
    listeners.forEach((f) => f());
  },
  getAuth: () => auth,
  setAuth: (v: typeof auth) => {
    auth = v;
    try {
      syncPhpliveVisitorPrefill(v);
      syncNewsletterFormPrefill(v);
    } catch {
      // never block auth UI / logout on prefill side-effects
    }
    listeners.forEach((f) => f());
  },
  getCartCount: () => cartCount,
  getCartTotal: () => cartTotal,
  getCartProducts: () => cartProducts,
  setCart: (count: number, total: number, products: any[]) => {
    cartCount = count;
    cartTotal = total;
    cartProducts = products;
    listeners.forEach((f) => f());
  },
  getLoading: () => loading,
  setLoading: (v: boolean) => {
    loading = v;
    listeners.forEach((f) => f());
  },
  getCheckoutPaymentId: () => checkoutPaymentId,
  setCheckoutPaymentId: (v: string | null) => {
    checkoutPaymentId = v;
  },
  getPaymentDetails: () => paymentDetails,
  setPaymentDetails: (v: Record<string, string> | null) => {
    paymentDetails = v;
  },
  getLastOrderId: () => lastOrderId,
  setLastOrderId: (v: string | null) => {
    lastOrderId = v;
  },
  getOrderReturnIntent: () => orderReturnIntent,
  setOrderReturnIntent: (v: OrderReturnIntent) => {
    orderReturnIntent = v;
    listeners.forEach((f) => f());
  },
  getOrdersFocusOrderId: () => ordersFocusOrderId,
  setOrdersFocusOrderId: (v: string | null) => {
    ordersFocusOrderId = v;
  },
  consumeOrdersFocusOrderId: () => {
    const id = ordersFocusOrderId;
    ordersFocusOrderId = null;
    return id;
  },
  getAccountView: () => accountView,
  setAccountView: (v: AccountView) => {
    accountView = v;
    listeners.forEach((f) => f());
    if (v && typeof window !== 'undefined' && !window.location.pathname.includes('/preview-proxy')) {
      window.dispatchEvent(new CustomEvent('sw:view-change', { detail: { view: v } }));
      const w = window as any;
      if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event: 'sw:view-change', view: v });
    }
  },
  getReturnTo: () => returnTo ?? readReturnToFromStorage(),
  setReturnTo: (v: AccountView) => {
    returnTo = v;
    syncReturnToStorage(v);
  },
  getToasts: () => toasts,
  addToast: (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id = Date.now();
    toasts = [...toasts, { message, type, id }];
    listeners.forEach((f) => f());
    setTimeout(() => {
      toasts = toasts.filter((t) => t.id !== id);
      listeners.forEach((f) => f());
    }, 3000);
  },
  hasInlinePlaceholder: (view: NonNullable<AccountView>) => inlinePlaceholders.has(view),
  registerInlinePlaceholder: (view: NonNullable<AccountView>) => { inlinePlaceholders.add(view); },
  subscribe: (f: Listener) => {
    listeners.push(f);
    return () => listeners.splice(listeners.indexOf(f), 1);
  },
};
